/*
 * elova-rail-progress.js
 *
 * The featured rail's progress bar is driven by `scroll-timeline: --rail-scroll x`
 * on the rail. A scroll timeline only advances when its scroll container can
 * actually scroll. At desktop widths the rail's content fits (measured
 * scrollWidth 1200 === clientWidth 1200 at both 1440 and 1280), so the timeline
 * never advances and the 2px track sat there permanently unfilled — an
 * affordance promising a scroll that does not exist.
 *
 * CSS cannot ask "does this element overflow", so this reports it: the section
 * carries `data-rail-overflow` only while the rail is actually scrollable, and
 * elova-featured-rail.css shows the track only in that case.
 *
 * Re-evaluated on resize (ResizeObserver on both the rail and its content) so
 * the bar appears and disappears correctly when the window is resized across
 * the threshold.
 *
 * No custom element, no dependencies. Loaded deferred from the section.
 */

(() => {
  const SECTION = '.elova-featured-rail';
  const RAIL = '.elova-featured-rail__rail';

  const sync = (rail) => {
    const section = rail.closest(SECTION);
    if (!section) return;
    // 1px tolerance: sub-pixel layout can leave a harmless fractional overflow.
    const scrollable = rail.scrollWidth - rail.clientWidth > 1;
    section.toggleAttribute('data-rail-overflow', scrollable);
  };

  const start = () => {
    const rails = document.querySelectorAll(RAIL);
    if (!rails.length) return;

    const ro = 'ResizeObserver' in window
      ? new ResizeObserver((entries) => {
          for (const entry of entries) {
            const rail = entry.target.matches(RAIL) ? entry.target : entry.target.closest(RAIL);
            if (rail) sync(rail);
          }
        })
      : null;

    rails.forEach((rail) => {
      sync(rail);
      if (!ro) return;
      ro.observe(rail);
      // The rail's own box can stay the same size while its content changes
      // (images finishing load, a variant swap); observe the first child too.
      if (rail.firstElementChild) ro.observe(rail.firstElementChild);
    });

    if (!ro) window.addEventListener('resize', () => rails.forEach(sync), { passive: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
